
import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  X, Clock, FileText, Paperclip, Edit2, Save, Building, Download, Trash2, File, Upload, Loader2, Hash, AlignLeft, Briefcase, Users, Calendar, AlertTriangle, MessageSquare, CheckCircle, Send, AtSign, Check, UserCheck, ChevronDown, RotateCcw, Tag, User as UserIcon, MessageCircle, Plus, MapPin
} from 'lucide-react';
import { Document, DocumentStatus, DocumentResult, TaskHistory, Contact, User, TaskComment, Attachment, UserRole } from '../types';
import { supabase } from '../supabase';
import { defaultDocumentTypes, defaultDocumentThemes } from '../data';
import SearchableSelect from './SearchableSelect';
import MentionInput from './MentionInput';
import FormattedText from './FormattedText';
import { useDraftState } from '../hooks/useDraftState';
import { extractMentionedUserIds } from '../utils/mentions';

interface DocumentDetailModalProps {
  letter: Document;
  users: User[];
  contacts: Contact[];
  onClose: () => void;
  onUpdateLetter: (id: string, updates: Partial<Document>) => void;
  onDeleteLetter: (id: string) => Promise<boolean>;
  onViewContact: (contactId: string) => void;
  currentUser: User;
  initialDeleteMode?: boolean;
}

const normalizeUuid = (val: string | null | undefined) => val && val.trim().length > 0 ? val : null;

// Função auxiliar para garantir que o anexo seja um objeto
const parseAttachment = (att: any): Attachment => {
  if (typeof att === 'string') {
    try {
      return JSON.parse(att);
    } catch (e) {
      console.error("Erro ao fazer parse do anexo:", att);
      return { id: 'error', name: 'Erro no arquivo', url: '#', timestamp: new Date().toISOString() };
    }
  }
  return att;
};

const DocumentDetailModal: React.FC<DocumentDetailModalProps> = ({ letter, users, contacts, onClose, onUpdateLetter, onDeleteLetter, onViewContact, currentUser, initialDeleteMode = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(initialDeleteMode);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');

  const [customThemes, setCustomThemes] = useState<string[]>([]);
  const [showNewTheme, setShowNewTheme] = useState(false);
  const [newThemeName, setNewThemeName] = useState('');

  useEffect(() => {
    if (letter.container_id) {
      supabase.from('document_themes').select('name').eq('container_id', letter.container_id).then(({ data }) => {
        if (data) setCustomThemes(data.map((d: any) => d.name));
      });
    }
  }, [letter.container_id]);

  const allThemes = [...new Set([...defaultDocumentThemes, ...customThemes])].sort();

  const handleAddTheme = async () => {
    const name = newThemeName.trim();
    if (!name || !letter.container_id) return;
    await supabase.from('document_themes').insert({ container_id: letter.container_id, name });
    setCustomThemes(prev => [...prev, name]);
    setEditedFields((prev: any) => ({ ...prev, theme: name }));
    setNewThemeName('');
    setShowNewTheme(false);
  };

  const [editedFields, setEditedFields, clearDraft] = useDraftState(`edit_doc_${letter.id}`, {
    subject: letter.subject || '',
    document_number: letter.document_number || '',
    document_type: letter.document_type || 'Ofício',
    recipient: letter.recipient || '',
    status: letter.status || DocumentStatus.ELABORATING,
    requester_type: letter.requester_type || 'contact',
    requester_id: letter.requester_id || '',
    responsible_id: letter.responsible_id || '',
    date: letter.date ? new Date(letter.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    response_text: letter.response_text || '',
    response_date: letter.response_date ? new Date(letter.response_date).toISOString().split('T')[0] : '',
    attachments: (letter.attachments || []).map(parseAttachment),
    theme: letter.theme || '',
    resultado: letter.resultado || '',
    received_by: letter.received_by || '',
    internal_requester: letter.internal_requester || '',
    deadline: letter.deadline ? new Date(letter.deadline).toISOString().split('T')[0] : '',
    notify_requester: letter.notify_requester || false,
    notify_updates: letter.notify_updates || false,
    stakeholders: letter.stakeholders || [],
    address_street: letter.address_street || '',
    address_number: letter.address_number || '',
    address_neighborhood: letter.address_neighborhood || '',
    address_city: letter.address_city || '',
    address_zipcode: letter.address_zipcode || '',
  });

  const requester = useMemo(() => 
    letter.requester_type === 'contact' ? contacts.find(c => c.id === letter.requester_id) : users.find(u => u.id === letter.requester_id), 
    [letter, contacts, users]
  );
  const responsible = useMemo(() => users.find(u => u.id === letter.responsible_id), [letter.responsible_id, users]);
  
  const handleSave = async () => {
    if(uploading) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      await onUpdateLetter(letter.id, {
        ...editedFields,
        requester_type: 'contact',
        requester_id: normalizeUuid(editedFields.requester_id),
        responsible_id: normalizeUuid(editedFields.responsible_id),
        date: editedFields.date ? new Date(editedFields.date).toISOString() : null,
        response_date: editedFields.response_date ? new Date(editedFields.response_date).toISOString() : null,
        deadline: editedFields.deadline ? new Date(editedFields.deadline).toISOString() : null,
        resultado: editedFields.resultado || null,
        theme: editedFields.theme || null,
        received_by: editedFields.received_by || null,
        internal_requester: editedFields.internal_requester || null,
        attachments: editedFields.attachments,
        notify_requester: editedFields.notify_requester,
        notify_updates: editedFields.notify_updates,
        stakeholders: editedFields.stakeholders,
        address_street: editedFields.address_street || null,
        address_number: editedFields.address_number || null,
        address_neighborhood: editedFields.address_neighborhood || null,
        address_city: editedFields.address_city || null,
        address_zipcode: editedFields.address_zipcode || null,
        history: [...(letter.history || []), { id: `h-doc-edt-${Date.now()}`, userId: currentUser.id, timestamp: new Date().toISOString(), action: `Atualizou dados do documento` }]
      });
      clearDraft();
      setIsEditing(false);
    } catch (err: any) { setErrorMsg("Erro ao salvar alterações."); } finally { setSaving(false); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    const comment: TaskComment = { id: `c-doc-${Date.now()}`, userId: currentUser.id, timestamp: new Date().toISOString(), text: newComment.trim() };
    try {
      await onUpdateLetter(letter.id, { comments: [...(letter.comments || []), comment] });
      const mentionedIds = extractMentionedUserIds(newComment);
      const uniqueMentions = mentionedIds.filter(id => id !== currentUser.id);
      if (uniqueMentions.length > 0) {
        const notifications = uniqueMentions.map(userId => ({
          user_id: userId, type: 'mention', title: 'Mencionaram você',
          description: `${currentUser.name} mencionou você no documento: "${letter.subject}"`,
          related_id: letter.id, read: false
        }));
        await supabase.from('notifications').insert(notifications);
      }
      setNewComment('');
    } catch (err) { console.error(err); }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    
    setUploading(true);
    try {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
        const filePath = `${currentUser.id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('cabinet-files')
            .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data } = supabase.storage.from('cabinet-files').getPublicUrl(filePath);

        const newAttachment: Attachment = {
            id: `doc-att-${Date.now()}`,
            name: file.name,
            url: data.publicUrl, 
            type: file.type.includes('image') ? 'image' : 'file',
            size: file.size,
            timestamp: new Date().toISOString()
        };
        
        setEditedFields((prev: any) => ({ 
            ...prev, 
            attachments: [...(prev.attachments || []), newAttachment] 
        }));
    } catch (error: any) {
        setErrorMsg('Erro ao fazer upload: ' + error.message);
    } finally {
        setUploading(false);
        e.target.value = '';
    }
  };

  const removeAttachment = (id: string) => {
    setEditedFields((prev: any) => ({ 
        ...prev, 
        attachments: (prev.attachments || []).filter((a: Attachment) => a.id !== id) 
    }));
  };

  const getStatusStyle = (status: DocumentStatus) => {
    switch (status) {
      case DocumentStatus.ELABORATING: return 'bg-amber-50 text-amber-600 border-amber-100';
      case DocumentStatus.FILED: return 'bg-parliament-50 text-parliament-600 border-parliament-100';
      case DocumentStatus.ANSWERED: return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case DocumentStatus.ARCHIVED: return 'bg-violet-50 text-violet-600 border-violet-100';
      default: return 'bg-slate-50 text-slate-600 border-slate-100';
    }
  };

  const deadlineDaysLeft = useMemo(() => {
    if (!letter.deadline) return null;
    const diff = new Date(letter.deadline).getTime() - Date.now();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }, [letter.deadline]);

  // Garante que a lista de visualização também esteja limpa
  const viewAttachments = (letter.attachments || []).map(parseAttachment);

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-navy-950/60 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div className="bg-white dark:bg-navy-900 w-full max-w-4xl max-h-[90vh] rounded-[28px] shadow-2xl flex flex-col overflow-hidden border border-neutral-200 dark:border-navy-800 relative" onClick={e => e.stopPropagation()}>
        
        {showDeleteConfirm && (
          <div className="absolute inset-0 z-[120] bg-navy-950/90 backdrop-blur-md flex flex-col items-center justify-center p-12 text-center animate-in fade-in">
             <Trash2 size={64} className="text-red-500 mb-6" />
             <h2 className="text-3xl font-black text-white mb-4 uppercase">Remover Documento?</h2>
             <div className="flex gap-4">
                <button onClick={() => setShowDeleteConfirm(false)} className="px-10 py-4 bg-white/10 text-white rounded-2xl font-black uppercase text-xs">Cancelar</button>
                <button onClick={async () => { await onDeleteLetter(letter.id); onClose(); }} className="px-10 py-4 bg-red-600 text-white rounded-2xl font-black uppercase text-xs">Confirmar</button>
             </div>
          </div>
        )}

        <div className="px-6 py-4 border-b border-slate-100 dark:border-navy-800 flex items-center justify-between shrink-0 bg-white dark:bg-navy-900">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-parliament-600 text-white shadow-lg"><FileText size={20} /></div>
            <h2 className="text-lg font-black text-slate-800 dark:text-white tracking-tight">{isEditing ? 'Configurar Expediente' : 'Expediente Legislativo'}</h2>
          </div>
          <div className="flex items-center gap-2">
             {!isEditing && (currentUser.permissions.canEditDocuments || currentUser.role !== UserRole.LEADERSHIP) && (
               <>
                 <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-5 py-2 bg-neutral-100 dark:bg-navy-800 text-neutral-600 rounded-xl font-black text-[10px] uppercase hover:bg-neutral-200 transition-colors">Editar</button>
                 <button onClick={() => setShowDeleteConfirm(true)} className="p-2 text-slate-300 hover:text-red-500 transition-all"><Trash2 size={18}/></button>
               </>
             )}
             <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full text-neutral-400"><X size={20} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar px-6 py-6 space-y-6">
          {isEditing ? (
             <div className="space-y-5 animate-in slide-in-from-left-4">
                {errorMsg && <div className="p-4 bg-red-50 text-red-600 rounded-2xl text-xs font-bold flex items-center gap-2"><AlertTriangle size={16}/> {errorMsg}</div>}
                
                <div className="space-y-6">
                   <h4 className="text-[10px] font-black text-navy-400 uppercase tracking-widest border-l-4 border-parliament-600 pl-3">Informações Básicas</h4>
                   <div className="space-y-2">
                     <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Assunto / Ementa</label>
                     <input value={editedFields.subject} onChange={e => setEditedFields({...editedFields, subject: e.target.value})} className="w-full text-2xl font-black bg-white dark:bg-navy-800 border border-slate-200 rounded-2xl px-6 py-5 outline-none dark:text-white" />
                   </div>
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Tipo</label>
                         <select value={editedFields.document_type} onChange={e => setEditedFields({...editedFields, document_type: e.target.value})} className="w-full bg-white dark:bg-navy-900 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white">
                           {defaultDocumentTypes.map(type => <option key={type} value={type}>{type}</option>)}
                         </select>
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Número Oficial</label>
                         <input value={editedFields.document_number} onChange={e => setEditedFields({...editedFields, document_number: e.target.value})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white" />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Data</label>
                         <input type="date" value={editedFields.date} onChange={e => setEditedFields({...editedFields, date: e.target.value})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white" />
                      </div>
                   </div>
                </div>

                <div className="space-y-6">
                   <h4 className="text-[10px] font-black text-navy-400 uppercase tracking-widest border-l-4 border-emerald-600 pl-3">Destino & Vínculos</h4>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Destinatário</label><input value={editedFields.recipient} onChange={e => setEditedFields({...editedFields, recipient: e.target.value})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white" /></div>
                      <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Assessor Responsável</label><select value={editedFields.responsible_id} onChange={e => setEditedFields({...editedFields, responsible_id: e.target.value})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white"><option value="">Não atribuído</option>{users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select></div>
                   </div>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Solicitante Original (CRM)</label>
                        <SearchableSelect options={contacts.map(c => ({ value: c.id, label: c.name }))} value={editedFields.requester_id} onChange={(val) => setEditedFields({...editedFields, requester_id: val})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white" />
                      </div>
                      <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Situação</label><select value={editedFields.status} onChange={e => setEditedFields({...editedFields, status: e.target.value as DocumentStatus})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white">{Object.values(DocumentStatus).map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                   </div>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Entrada no Gabinete (Recebido por)</label><input list="edit-doc-team-names" value={editedFields.received_by} onChange={e => setEditedFields({...editedFields, received_by: e.target.value})} placeholder="Assessor que recebeu o pedido" className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white" /></div>
                      <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Requerente Interno (Gabinete)</label><input list="edit-doc-team-names" value={editedFields.internal_requester} onChange={e => setEditedFields({...editedFields, internal_requester: e.target.value})} placeholder="Funcionário que fez o pedido" className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white" /></div>
                      <datalist id="edit-doc-team-names">
                        {users.map(u => <option key={u.id} value={u.name} />)}
                      </datalist>
                   </div>
                   <div className="space-y-2">
                     <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Partes Interessadas (Stakeholders)</label>
                     <div className="grid grid-cols-2 gap-2">
                       {users.map(u => (
                         <label key={u.id} className="flex items-center gap-2 p-3 bg-white dark:bg-navy-800 border border-slate-100 dark:border-navy-700 rounded-xl">
                           <input type="checkbox" checked={editedFields.stakeholders?.includes(u.id) || false} onChange={e => {
                             const current = editedFields.stakeholders || [];
                             setEditedFields({...editedFields, stakeholders: e.target.checked ? [...current, u.id] : current.filter((id: string) => id !== u.id)});
                           }} className="rounded" />
                           <span className="text-sm font-bold dark:text-white">{u.name}</span>
                         </label>
                       ))}
                     </div>
                   </div>
                   <div className="space-y-2">
                     <label className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/10 rounded-2xl border border-amber-100 dark:border-amber-900/30 cursor-pointer">
                       <input type="checkbox" checked={editedFields.notify_updates} onChange={e => setEditedFields({...editedFields, notify_updates: e.target.checked, notify_requester: e.target.checked ? editedFields.notify_requester : false})} className="rounded" />
                       <div>
                         <span className="text-sm font-bold text-amber-800 dark:text-amber-300">Avisar sobre respostas/movimentação</span>
                         <p className="text-[10px] text-amber-600">Responsável e stakeholders recebem notificação a cada movimentação</p>
                       </div>
                     </label>
                     {editedFields.notify_updates && (
                       <label className="flex items-center gap-3 p-3 ml-6 bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl border border-emerald-100 dark:border-emerald-900/30 cursor-pointer animate-in slide-in-from-top-2">
                         <input type="checkbox" checked={editedFields.notify_requester} onChange={e => setEditedFields({...editedFields, notify_requester: e.target.checked})} className="rounded" />
                         <div>
                           <span className="text-sm font-bold text-emerald-800 dark:text-emerald-300">Incluir o solicitante</span>
                           <p className="text-[10px] text-emerald-600">O cidadão que solicitou também será informado via WhatsApp</p>
                         </div>
                       </label>
                     )}
                   </div>
                </div>

                <div className="space-y-6">
                   <h4 className="text-[10px] font-black text-navy-400 uppercase tracking-widest border-l-4 border-violet-600 pl-3">Tema & Prazo</h4>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Tema</label>
                        <div className="flex gap-2">
                          <select value={editedFields.theme} onChange={e => setEditedFields({...editedFields, theme: e.target.value})} className="flex-1 bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white">
                            <option value="">Sem tema</option>
                            {allThemes.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          {!showNewTheme ? (
                            <button type="button" onClick={() => setShowNewTheme(true)} className="p-3 bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl text-parliament-600 hover:bg-parliament-50"><Plus size={18}/></button>
                          ) : (
                            <div className="flex gap-1">
                              <input value={newThemeName} onChange={e => setNewThemeName(e.target.value)} placeholder="Novo tema..." className="w-32 bg-white dark:bg-navy-950 border border-neutral-100 rounded-xl px-3 py-2 text-sm font-bold dark:text-white" />
                              <button type="button" onClick={handleAddTheme} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg"><Check size={16}/></button>
                              <button type="button" onClick={() => { setShowNewTheme(false); setNewThemeName(''); }} className="p-2 text-slate-400 hover:bg-slate-50 rounded-lg"><X size={16}/></button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Prazo Fatal</label><input type="date" value={editedFields.deadline} onChange={e => setEditedFields({...editedFields, deadline: e.target.value})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white" /></div>
                   </div>
                </div>

                <div className="space-y-6">
                   <h4 className="text-[10px] font-black text-navy-400 uppercase tracking-widest border-l-4 border-rose-600 pl-3">Endereço (aparece no Mapa)</h4>
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="md:col-span-2 space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Rua / Logradouro</label><input value={editedFields.address_street} onChange={e => setEditedFields({...editedFields, address_street: e.target.value})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white" /></div>
                      <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Número</label><input value={editedFields.address_number} onChange={e => setEditedFields({...editedFields, address_number: e.target.value})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white" /></div>
                   </div>
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Bairro</label><input value={editedFields.address_neighborhood} onChange={e => setEditedFields({...editedFields, address_neighborhood: e.target.value})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white" /></div>
                      <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Cidade</label><input value={editedFields.address_city} onChange={e => setEditedFields({...editedFields, address_city: e.target.value})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white" /></div>
                      <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">CEP</label><input value={editedFields.address_zipcode} onChange={e => setEditedFields({...editedFields, address_zipcode: e.target.value})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white" /></div>
                   </div>
                </div>

                <div className="space-y-6">
                   <h4 className="text-[10px] font-black text-navy-400 uppercase tracking-widest border-l-4 border-amber-600 pl-3">Resposta & Conclusão</h4>
                   <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Texto da Resposta (Órgão)</label>
                        <textarea value={editedFields.response_text} onChange={e => setEditedFields({...editedFields, response_text: e.target.value})} rows={5} className="w-full bg-white dark:bg-navy-900 border border-slate-200 rounded-3xl p-6 outline-none dark:text-white text-sm font-medium shadow-inner" placeholder="Caso o órgão tenha respondido, insira o texto aqui..." />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Data da Devolutiva</label>
                          <input type="date" value={editedFields.response_date} onChange={e => setEditedFields({...editedFields, response_date: e.target.value})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Efetividade Política</label>
                          <select value={editedFields.resultado} onChange={e => setEditedFields({...editedFields, resultado: e.target.value})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-2xl px-6 py-4 text-sm font-bold dark:text-white">
                            <option value="">Sem resultado</option>
                            {Object.values(DocumentResult).map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>
                      </div>
                   </div>
                </div>

                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><Paperclip size={14}/> Anexos</label>
                        <label className="cursor-pointer text-[10px] font-bold text-parliament-600 uppercase hover:underline flex items-center gap-1">
                           {uploading ? <Loader2 size={12} className="animate-spin"/> : <Plus size={12}/>} 
                           {uploading ? 'Enviando...' : 'Adicionar Arquivo'}
                           <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} />
                        </label>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {editedFields.attachments && editedFields.attachments.length > 0 ? (
                            editedFields.attachments.map((rawAtt: any) => {
                                const att = parseAttachment(rawAtt);
                                return (
                                    <div key={att.id || Math.random()} className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-navy-800 border border-slate-200 dark:border-navy-700 rounded-xl shadow-sm">
                                        <File size={14} className="text-slate-400" />
                                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300 truncate max-w-[150px]">{att.name || 'Sem nome'}</span>
                                        <button onClick={() => removeAttachment(att.id)} className="text-slate-400 hover:text-red-500"><X size={12}/></button>
                                    </div>
                                );
                            })
                        ) : (
                            <p className="text-xs text-slate-400 italic pl-1">Nenhum anexo.</p>
                        )}
                    </div>
                 </div>

                <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                  <button onClick={() => { setIsEditing(false); clearDraft(); }} className="px-8 py-3 font-bold text-slate-400">Descartar</button>
                  <button onClick={handleSave} disabled={saving || uploading} className="bg-navy-950 dark:bg-parliament-600 text-white px-12 py-5 rounded-3xl font-black text-[11px] uppercase shadow-2xl flex items-center gap-3 disabled:opacity-50">
                    {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} SALVAR ALTERAÇÕES
                  </button>
                </div>
             </div>
          ) : (
            <div className="space-y-12 animate-in fade-in">
                <div className="flex flex-col gap-2">
                   <div className="flex items-center gap-3 flex-wrap">
                     <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border w-fit ${getStatusStyle(letter.status)}`}>{letter.status}</span>
                     {letter.resultado && <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border bg-rose-50 text-rose-600 border-rose-100">{letter.resultado}</span>}
                     {letter.theme && <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border bg-violet-50 text-violet-600 border-violet-100">{letter.theme}</span>}
                     <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Tag size={12}/> {letter.document_type.toUpperCase()} nº {letter.document_number}</span>
                     <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Calendar size={12}/> {new Date(letter.date).toLocaleDateString('pt-BR')}</span>
                   </div>
                   {deadlineDaysLeft !== null && (
                     <div className={`flex items-center gap-2 px-4 py-2 rounded-xl w-fit text-xs font-black uppercase ${deadlineDaysLeft <= 0 ? 'bg-red-100 text-red-700 animate-pulse' : deadlineDaysLeft <= 7 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                       <Clock size={14}/>
                       {deadlineDaysLeft <= 0 ? `PRAZO VENCIDO (${Math.abs(deadlineDaysLeft)} dias)` : `Prazo: ${deadlineDaysLeft} dias restantes (${new Date(letter.deadline!).toLocaleDateString('pt-BR')})`}
                     </div>
                   )}
                   <h1 className="text-5xl font-black text-slate-900 dark:text-white tracking-tighter leading-tight">{letter.subject || 'Sem Assunto'}</h1>
                </div>

                {/* DETALHES COMPLETOS VISUALIZAÇÃO */}
                <div className="space-y-6">
                   <h4 className="text-[10px] font-black text-navy-400 uppercase tracking-widest border-l-4 border-parliament-600 pl-3">Detalhes do Documento</h4>
                   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      <div className="bg-white dark:bg-navy-800 p-6 rounded-[24px] border border-slate-100 dark:border-navy-700 shadow-sm space-y-4">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Requerente Original</p>
                          <div className="flex items-center gap-4">
                             <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 flex items-center justify-center font-black text-lg shadow-inner">{requester?.name.charAt(0) || '?'}</div>
                             <div><p className="font-bold text-navy-950 dark:text-white text-base line-clamp-1">{requester?.name || 'Não identificado'}</p><p className="text-xs font-bold text-emerald-500 uppercase">Solicitante</p></div>
                          </div>
                      </div>
                      <div className="bg-white dark:bg-navy-800 p-6 rounded-[24px] border border-slate-100 dark:border-navy-700 shadow-sm space-y-4">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Assessor Responsável</p>
                          <div className="flex items-center gap-4">
                             <div className="w-12 h-12 rounded-2xl bg-parliament-50 dark:bg-navy-800 text-parliament-600 flex items-center justify-center font-black text-lg shadow-inner">{responsible?.name.charAt(0) || '?'}</div>
                             <div><p className="font-bold text-navy-950 dark:text-white text-base line-clamp-1">{responsible?.name || 'Não atribuído'}</p><p className="text-xs font-bold text-parliament-600 uppercase">Gabinete</p></div>
                          </div>
                      </div>
                      <div className="bg-white dark:bg-navy-800 p-6 rounded-[24px] border border-slate-100 dark:border-navy-700 shadow-sm space-y-4">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Destino / Órgão</p>
                          <div className="flex items-center gap-4">
                             <div className="w-12 h-12 rounded-2xl bg-slate-50 dark:bg-navy-700 text-slate-400 flex items-center justify-center"><Building size={24} /></div>
                             <div><p className="font-black text-navy-950 dark:text-white text-base line-clamp-1">{letter.recipient || 'Não informado'}</p><p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Destinatário</p></div>
                          </div>
                      </div>
                   </div>
                   {(letter.received_by || letter.internal_requester) && (
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
                        {letter.received_by && (
                          <div className="bg-white dark:bg-navy-800 p-5 rounded-[24px] border border-slate-100 dark:border-navy-700 shadow-sm">
                             <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Entrada no Gabinete</p>
                             <p className="text-sm font-bold text-navy-950 dark:text-white">{letter.received_by}</p>
                          </div>
                        )}
                        {letter.internal_requester && (
                          <div className="bg-white dark:bg-navy-800 p-5 rounded-[24px] border border-slate-100 dark:border-navy-700 shadow-sm">
                             <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Requerente Interno</p>
                             <p className="text-sm font-bold text-navy-950 dark:text-white">{letter.internal_requester}</p>
                          </div>
                        )}
                     </div>
                   )}
                   {(letter.stakeholders && letter.stakeholders.length > 0) && (
                     <div className="mt-4">
                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Partes Interessadas</p>
                       <div className="flex flex-wrap gap-2">
                         {letter.stakeholders.map(sid => {
                           const su = users.find(u => u.id === sid);
                           return su ? (
                             <span key={sid} className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-navy-800 border border-slate-100 dark:border-navy-700 rounded-xl">
                               <div className="w-6 h-6 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center text-[10px] font-black">{su.name.charAt(0)}</div>
                               <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{su.name}</span>
                             </span>
                           ) : null;
                         })}
                       </div>
                     </div>
                   )}
                   {letter.notify_updates && (
                     <div className="flex flex-wrap gap-3 mt-4">
                       <span className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/10 text-amber-600 rounded-xl text-[10px] font-black uppercase tracking-widest border border-amber-100">
                         <AtSign size={14}/> Avisos de movimentação ativos
                       </span>
                       {letter.notify_requester && (
                         <span className="flex items-center gap-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/10 text-emerald-600 rounded-xl text-[10px] font-black uppercase tracking-widest border border-emerald-100">
                           <MessageCircle size={14}/> Solicitante incluído
                         </span>
                       )}
                     </div>
                   )}
                   {letter.address_street && (
                     <div className="bg-white dark:bg-navy-800 p-5 rounded-[24px] border border-slate-100 dark:border-navy-700 shadow-sm mt-4">
                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Endereço do Ofício</p>
                       <p className="text-sm font-bold text-navy-950 dark:text-white">
                         {letter.address_street}{letter.address_number ? `, ${letter.address_number}` : ''}
                         {letter.address_neighborhood ? ` — ${letter.address_neighborhood}` : ''}
                         {letter.address_city ? `, ${letter.address_city}` : ''}
                       </p>
                     </div>
                   )}
                </div>

                <div className="space-y-6 animate-in slide-in-from-bottom-4">
                     <h3 className="text-xl font-black dark:text-white flex items-center gap-2 border-l-4 border-emerald-500 pl-4"><MessageCircle size={22} className="text-emerald-500"/> Resposta Oficial do Órgão</h3>
                     {letter.response_text ? (
                        <div className="p-8 bg-emerald-50 dark:bg-emerald-900/10 rounded-[32px] border border-emerald-100 dark:border-emerald-800 shadow-sm">
                            <div className="flex items-center gap-2 text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-4">
                               <Calendar size={14}/> Recebida em: {letter.response_date ? new Date(letter.response_date).toLocaleDateString('pt-BR') : 'Data não informada'}
                            </div>
                            <p className="text-lg text-slate-800 dark:text-slate-200 leading-relaxed font-medium italic">
                               "{letter.response_text}"
                            </p>
                        </div>
                     ) : (
                        <div className="p-8 bg-neutral-50 dark:bg-navy-950 rounded-[32px] border border-slate-100 dark:border-navy-800 text-center">
                           <p className="text-slate-400 text-sm font-medium italic">Ainda não há resposta registrada para este documento.</p>
                        </div>
                     )}
                </div>

                {/* Anexos (Visualização) */}
                {viewAttachments.length > 0 && (
                    <div className="space-y-6">
                        <h3 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-3"><Paperclip size={24} className="text-slate-400"/> Anexos</h3>
                        <div className="flex flex-wrap gap-4">
                            {viewAttachments.map((att: Attachment) => (
                                <div key={att.id || Math.random()} className="flex items-center gap-3 p-4 bg-white dark:bg-navy-900 border border-slate-100 dark:border-navy-800 rounded-2xl shadow-sm">
                                    <div className="p-2 bg-slate-50 dark:bg-navy-800 rounded-lg text-slate-400"><File size={20}/></div>
                                    <div>
                                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300 max-w-[200px] truncate" title={att.name}>{att.name || 'Sem nome'}</p>
                                        <p className="text-[10px] text-slate-400">{att.timestamp ? new Date(att.timestamp).toLocaleDateString() : '-'}</p>
                                    </div>
                                    <a href={att.url} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-300 hover:text-parliament-600"><Download size={16}/></a>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="space-y-8 pt-8 border-t border-slate-100 dark:border-navy-800">
                  <h3 className="text-xl font-black dark:text-white flex items-center gap-2"><MessageSquare size={22} className="text-parliament-600" /> Notas Internas</h3>
                  <div className="space-y-4 mb-6">
                    {letter.comments && letter.comments.length > 0 ? letter.comments.map(c => {
                       const user = users.find(u => u.id === c.userId);
                       return (
                         <div key={c.id} className="p-5 bg-white dark:bg-navy-800 rounded-3xl border border-slate-100 dark:border-navy-700 shadow-sm">
                            <div className="flex justify-between mb-2">
                               <span className="text-[10px] font-black text-parliament-600 uppercase tracking-widest">{user?.name || 'Sistema'}</span>
                               <span className="text-[9px] font-bold text-slate-400">{new Date(c.timestamp).toLocaleString('pt-BR')}</span>
                            </div>
                            <p className="text-sm font-medium text-slate-700 dark:text-slate-300"><FormattedText text={c.text} /></p>
                         </div>
                       );
                    }) : <p className="text-center py-10 text-slate-400 text-sm italic">Nenhuma observação interna.</p>}
                  </div>
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-xl bg-parliament-50 dark:bg-navy-800 text-parliament-600 flex items-center justify-center shrink-0"><MessageSquare size={18}/></div>
                    <div className="flex-1 relative">
                      <MentionInput 
                        value={newComment} onChange={setNewComment} users={users} placeholder="Adicionar nota..." className="w-full bg-white dark:bg-navy-900 border border-slate-200 dark:border-navy-800 rounded-3xl px-6 py-4 outline-none focus:ring-4 focus:ring-parliament-50 dark:text-white shadow-inner resize-none text-sm font-medium transition-all"
                        onKeyDown={(e) => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                      />
                      <button onClick={handleAddComment} disabled={!newComment.trim()} className="absolute right-4 bottom-4 p-2.5 bg-navy-950 dark:bg-parliament-600 text-white rounded-xl shadow-lg"><Send size={16} /></button>
                    </div>
                  </div>
                </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentDetailModal;
